import { createSelector } from '@reduxjs/toolkit';
import { Parking, TimePeriod } from 'src/types';
import { getDateTime } from 'src/utils/getDateTime';
import { splitTime } from 'src/utils/splitTime';

import { StoreState } from '../types';

import { ParkingState } from './types';

const emptyTimePeriod: TimePeriod = {
  from: undefined,
  to: undefined,
};

export const getParkingState = (state: StoreState): ParkingState =>
  state.parking;

export const getParkingLocation = createSelector(getParkingState, parking => ({
  loading: parking.locationLoading,
  error: parking.locationError,
  town: parking.town,
  building: parking.building,
  floor: parking.floor,
}));

export const getParkingPlaces = createSelector(
  getParkingState,
  parking => parking.bookingObjects,
);

export const getParkingTown = createSelector(
  getParkingState,
  parking => parking.town,
);

export const getParkingDate = createSelector(
  getParkingState,
  parking => parking.date,
);

export const getParkingTime = createSelector(
  getParkingState,
  parking => parking.time,
);

export const getParkingTimeSplitted = createSelector(getParkingTime, time =>
  time.split('—'),
);

export const getParkingDateError = createSelector(
  getParkingState,
  parking => parking.dateError,
);

export const getParkingTimeError = createSelector(
  getParkingState,
  parking => parking.timeError,
);

export const getParkingTimePeriodUTC = createSelector(
  getParkingDate,
  getParkingTown,
  getParkingTimeSplitted,
  getParkingTimeError,
  (date, town, [timeFrom, timeTo], timeError) => {
    if (!date) return emptyTimePeriod;

    if (timeError) return emptyTimePeriod;

    if (!town) return emptyTimePeriod;

    const result: TimePeriod = {
      from: undefined,
      to: undefined,
    };

    const [fromHours, fromMinutes] = splitTime(timeFrom);
    const [toHours, toMinutes] = splitTime(timeTo);

    const fromDate = getDateTime({
      date: new Date(date),
      hours: fromHours,
      minutes: fromMinutes,
      seconds: 0,
      milliseconds: 0,
      timezoneOffset: town.timeZone,
      toUTC: true,
    });
    const toDate = getDateTime({
      date: new Date(date),
      hours: toHours,
      minutes: toMinutes,
      seconds: 0,
      milliseconds: 0,
      timezoneOffset: town.timeZone,
      toUTC: true,
    });
    if (toDate < fromDate) toDate.setDate(toDate.getDate() + 1);

    result.from = fromDate.toISOString();
    result.to = toDate.toISOString();

    return result;
  },
);

export const getParkingTimePeriodLocal = createSelector(
  getParkingDate,
  getParkingTown,
  getParkingTimeSplitted,
  getParkingTimeError,
  (date, town, [timeFrom, timeTo], timeError) => {
    if (!date) return emptyTimePeriod;

    if (timeError) return emptyTimePeriod;

    if (!town) return emptyTimePeriod;

    const result: TimePeriod = {
      from: undefined,
      to: undefined,
    };

    const [fromHours, fromMinutes] = splitTime(timeFrom);
    const [toHours, toMinutes] = splitTime(timeTo);

    const fromDate = getDateTime({
      date: new Date(date),
      hours: fromHours,
      minutes: fromMinutes,
      seconds: 0,
      milliseconds: 0,
    });
    const toDate = getDateTime({
      date: new Date(date),
      hours: toHours,
      minutes: toMinutes,
      seconds: 0,
      milliseconds: 0,
    });

    result.from = fromDate.toISOString();
    result.to = toDate.toISOString();

    return result;
  },
);

export const getCurrentParkingPlace = createSelector(
  getParkingState,
  parking =>
    parking.parkingId
      ? ((parking.bookingObjects.get(parking.parkingId) as Parking) ??
        undefined)
      : undefined,
);
